import { Rgb, Scene, WallpaperStyleInfo, darken, fbm, lighten, luminance, mix, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'isometric', label: 'Isometric',
  blurb: 'A landscape of stacked isometric cubes shaded on three faces',
  params: [ share( 'size', 'Size', 40 ), share( 'relief', 'Relief', 50 ) ],
};

/** the tile at a chosen lightness: a mix toward white or black is linear in luminance, so the target is exact */
function toneOf( c: Rgb, target: number ): Rgb {
  const l = luminance( c );
  return target > l ? lighten( c, ( target - l ) / Math.max( 0.001, 1 - l ) ) : darken( c, ( l - target ) / Math.max( 0.001, l ) );
}

/** three tones of one tile, the tile itself in the middle, the window slid to fit near white and near black */
function strata( c: Rgb ): Rgb[] {
  const lo = Math.max( 0.05, Math.min( 0.94, luminance( c ) + 0.13 ) - 0.26 );
  const hi = Math.min( 0.94, lo + 0.26 );
  return [ toneOf( c, lo ), toneOf( c, ( lo + hi ) / 2 ), toneOf( c, hi ) ];
}

/** the three faces of one tile, already at their own lightness */
interface Faces { top: Rgb; left: Rgb; right: Rgb; }

/**
 * Isometric: a voxel landscape on the 2:1 grid of pixel art. Every column's height is two octaves of
 * value noise rounded to whole cubes, so the terrain is stepped by design, and the steps read as
 * hills only while the noise stays low frequency. The columns are drawn back to front, each as three
 * faces lit from one fixed corner, with a seam at every cube joint so a tall stack reads as cubes
 * and not as a slab. Vectors only, no offscreen pass.
 */
export function draw( { ctx, w, h, box, colors, rand, p }: Scene ): void {
  const hair = Math.max( 1, box.w / 1920 );

  // Size: 40 columns across the box at 0, 10 at 100
  const cols = Math.round( 40 - p[ 0 ] * 30 );
  const cw = box.w / cols;
  const hw = cw / 2, hh = cw / 4;
  // the upright edge of a cube in a 2:1 projection, so one block reads as a cube and not as a slab
  const ch = hw * Math.sqrt( 5 ) / 2;
  // Relief: how high the terrain climbs, at most one cube per tile, so a ten colour palette builds
  // a ten storey landscape and every storey is its own colour
  const levels = 1 + Math.round( p[ 1 ] * ( colors.length - 1 ) );
  // level k wears tile k; a palette of one gets three tones of it so the terraces still read
  const tiles = colors.length > 1 ? colors : strata( colors[ 0 ] );

  // the ground plane sits below the middle of the box, because the stacks only grow upward
  const ox = box.x + box.w / 2;
  const oy = box.y + box.h * 0.56;

  // The height field is read in grid space, so the hills lie flat on the ground plane, and at a
  // scale counted in cubes rather than in pixels: the same seed then draws the same terrain at
  // every resolution, and a phone crop is the middle of the picture the desktop shows. A hill runs
  // about six cubes; two octaves is as spiky as it may get before the steps stop reading as terrain
  const reach = cols * 4;
  const field = fbm( rand, Math.max( 3, Math.min( 14, Math.round( cols * 2 / 3 ) ) ), 2, 0.45 );
  const sample = ( i: number, j: number ) => field( 0.5 + i / reach, 0.5 + j / reach );

  // the samples crowd the middle of 0..1, so the field is stretched over the range it takes across
  // the design box: without it the terrain would never reach the floor or the tallest stack. The
  // window is measured over the box alone, so a wider canvas extends this terrain instead of a new one
  // the box counted in grid steps: `cols` of them from the middle to either side, and, since the
  // ground plane sits at 0.56 of the height, 1.26 rows back up the screen for every one forward
  const across = cols, back = Math.round( cols * 1.26 ), front = cols;
  let lo = 1, hi = 0;
  const bound = Math.ceil( cols * 1.2 );
  for ( let i = -bound; i <= bound; i++ ) {
    for ( let j = -bound; j <= bound; j++ ) {
      const u = i - j, v = i + j;
      if ( u < -across || u > across || v < -back || v > front ) continue;
      const n = sample( i, j );
      if ( n < lo ) lo = n;
      if ( n > hi ) hi = n;
    }
  }
  const spread = Math.max( 1e-6, hi - lo );

  // the three faces are fixed offsets off the tile, not a walk toward white: a bright tile has no
  // room left toward white, so the light has to come out of the two walls instead, and darkening
  // keeps the hue where lightening would wash the whole picture out
  const facesOf = ( c: Rgb ): Faces => {
    // a near black tile has nothing to darken either, so it is lifted first and shaded after; the
    // lift is small and only for the very darkest tiles, or a dark first storey comes out grey
    // instead of keeping the tile's own colour
    const lift = Math.max( 0, 0.12 - luminance( c ) ) / 0.12;
    const base = lift > 0 ? lighten( c, lift * 0.2 ) : c;
    return { top: lighten( base, 0.1 ), left: darken( base, 0.18 ), right: darken( base, 0.42 ) };
  };
  const shades: Faces[] = tiles.map( facesOf );

  // The tiles' order climbs the terrain (owner, 2026-09-22): level zero is the ground the landscape
  // stands on, the first cube up is the first tile, the next the second. The ground is a near black
  // tinted with the first tile, and it is pushed further down when the first storey sits too close
  // to it, or a dark palette would stand dark cubes on a dark floor
  const groundTint = mix( { r: 6, g: 6, b: 10 }, colors[ 0 ], 0.14 );
  const firstStorey = luminance( shades[ 0 ].top );
  const ground = firstStorey - luminance( groundTint ) < 0.12
    ? toneOf( groundTint, Math.max( 0.015, firstStorey - 0.16 ) )
    : groundTint;
  ctx.fillStyle = rgba( ground, 1 );
  ctx.fillRect( 0, 0, w, h );

  // every fill is built once in the column's own frame (the origin is the centre of its top face),
  // so a few thousand columns share them instead of each building its own
  const topFill = shades.map( f => rgba( f.top, 1 ) );
  // the cube grid on the tops: without it a plateau is one sheet of colour, not cubes side by side
  const topEdge = shades.map( f => luminance( f.top ) < 0.3 ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)' );
  const wall = ( c: Rgb, height: number ): CanvasGradient => {
    const g = ctx.createLinearGradient( 0, 0, 0, height );
    g.addColorStop( 0, rgba( lighten( c, 0.04 ), 1 ) );
    g.addColorStop( 1, rgba( darken( c, 0.16 ), 1 ) );
    return g;
  };
  const leftFill: CanvasGradient[][] = shades.map( () => [] );
  const rightFill: CanvasGradient[][] = shades.map( () => [] );
  for ( let t = 0; t < shades.length; t++ ) {
    for ( let k = 2; k <= levels; k++ ) {
      leftFill[ t ][ k ] = wall( shades[ t ].left, k * ch + hh );
      rightFill[ t ][ k ] = wall( shades[ t ].right, k * ch + hh );
    }
  }
  // the flat middle of that gradient. It closes the antialiased seam along every face, and a wall
  // one cube tall wears it outright: a gradient costs three times a colour to paint, and over that
  // little height there is nothing in it to see
  const leftLine = shades.map( f => rgba( darken( f.left, 0.06 ), 1 ) );
  const rightLine = shades.map( f => rgba( darken( f.right, 0.06 ), 1 ) );
  // the cube joints up a stack: a hairline the wall cannot swallow, dark on a light tile, light on a dark one
  const seam = tiles.map( c => luminance( c ) < 0.3 ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.2)' );

  // what a height zero column lays down: the ground a shade off itself, with the grid still on it,
  // and lifted rather than darkened when it is already near black
  const deep = luminance( ground ) > 0.14;
  const floorFill = rgba( deep ? darken( ground, 0.1 ) : lighten( ground, 0.07 ), 1 );
  const floorEdge = rgba( deep ? darken( ground, 0.19 ) : lighten( ground, 0.15 ), 1 );

  // u = i - j runs across the screen, v = i + j runs into it; the two always share their parity.
  // The rows reach past the canvas on every side, and far enough below it that a tall stack
  // standing off screen still leans into the frame
  const u0 = Math.floor( ( 0 - ox ) / hw ) - 2;
  const u1 = Math.ceil( ( w - ox ) / hw ) + 2;
  const v0 = Math.floor( ( 0 - oy - hh ) / hh ) - 2;
  const v1 = Math.ceil( ( h - oy + levels * ch + hh ) / hh ) + 2;
  // ascending v is back to front, and two columns of one v never overlap, so this is the whole
  // painter's order the scene needs
  for ( let v = v0; v <= v1; v++ ) {
    for ( let u = u0 + ( ( u0 - v ) & 1 ); u <= u1; u += 2 ) {
      const n = sample( ( u + v ) / 2, ( v - u ) / 2 );
      // the slight bend leaves more of the ground tile showing than a straight read would
      const t0 = Math.max( 0, Math.min( 1, ( n - lo ) / spread ) );
      const k = Math.round( Math.pow( t0, 1.15 ) * levels );
      const cy = oy + v * hh;
      // the rows below the canvas are only there for the tall stacks, so the short ones stop here
      if ( cy - k * ch - hh > h ) continue;
      ctx.setTransform( 1, 0, 0, 1, ox + u * hw, cy - k * ch );
      ctx.lineWidth = hair;
      if ( k === 0 ) {
        ctx.beginPath();
        ctx.moveTo( 0, -hh );
        ctx.lineTo( hw, 0 );
        ctx.lineTo( 0, hh );
        ctx.lineTo( -hw, 0 );
        ctx.closePath();
        ctx.fillStyle = floorFill;
        ctx.strokeStyle = floorEdge;
        ctx.fill();
        ctx.stroke();
        continue;
      }
      const t = Math.min( tiles.length - 1, k - 1 );
      const tall = k * ch;
      // every face is stroked with its own fill, the way Low Poly does, so no antialiased seam lets
      // the ground through between two faces of one block
      ctx.beginPath();
      ctx.moveTo( -hw, 0 );
      ctx.lineTo( 0, hh );
      ctx.lineTo( 0, hh + tall );
      ctx.lineTo( -hw, tall );
      ctx.closePath();
      ctx.fillStyle = k > 1 ? leftFill[ t ][ k ] : leftLine[ t ];
      ctx.strokeStyle = leftLine[ t ];
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo( hw, 0 );
      ctx.lineTo( 0, hh );
      ctx.lineTo( 0, hh + tall );
      ctx.lineTo( hw, tall );
      ctx.closePath();
      ctx.fillStyle = k > 1 ? rightFill[ t ][ k ] : rightLine[ t ];
      ctx.strokeStyle = rightLine[ t ];
      ctx.fill();
      ctx.stroke();
      if ( k > 1 ) {
        ctx.beginPath();
        for ( let d = 1; d < k; d++ ) {
          ctx.moveTo( -hw, d * ch );
          ctx.lineTo( 0, d * ch + hh );
          ctx.lineTo( hw, d * ch );
        }
        ctx.strokeStyle = seam[ t ];
        ctx.stroke();
      }
      // the top face last, so it lands on the upper edge of its own two walls
      ctx.beginPath();
      ctx.moveTo( 0, -hh );
      ctx.lineTo( hw, 0 );
      ctx.lineTo( 0, hh );
      ctx.lineTo( -hw, 0 );
      ctx.closePath();
      ctx.fillStyle = topFill[ t ];
      ctx.strokeStyle = topEdge[ t ];
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.setTransform( 1, 0, 0, 1, 0, 0 );
}
